import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import { shuffle } from 'lodash'; // Importing the shuffle function from a library like Lodash
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DialogPromptMatchWordPicture } from '../../api/DialogPromptApi';
import { UserDialogPromptApi } from '../../api/UserDialogPromptApi';
import { IVocabularyWord } from '../../api/VocabularyWordApi';
import AppBarContext from '../../context/AppBarContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { getAudioByText } from './DialogPromptComponent';


interface DialogPromptMatchWordPictureComponentProps {
    dialogPrompt: DialogPromptMatchWordPicture;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

/**
 * @deprecated Use DialogPromptMatchGameComponent for new exercises. This component remains for legacy prompts.
 */
const QuestionImageDisplay: React.FC<{
    src: string | undefined;
    alt: string;
    onLightboxOpen: () => void;
}> = ({ src, alt, onLightboxOpen }) => {
    const [hovering, setHovering] = React.useState(false);
    const [imageSize, setImageSize] = React.useState<{ width: number; height: number } | null>(null);
    const imgRef = React.useRef<HTMLImageElement>(null);

    React.useEffect(() => {
        if (imgRef.current?.complete && imgRef.current.naturalHeight !== 0) {
            setImageSize({ width: imgRef.current.naturalWidth, height: imgRef.current.naturalHeight });
        }
    }, []);

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                onLoad={(e) => {
                    const img = e.currentTarget;
                    setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
                }}
                onClick={onLightboxOpen}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                style={{
                    maxHeight: '200px',
                    maxWidth: '200px',
                    height: 'auto',
                    width: 'auto',
                    display: 'block',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease',
                    opacity: hovering ? 0.85 : 1,
                    borderRadius: '10px',
                }}
            />
            {hovering && imageSize && (
                <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    pointerEvents: 'none',
                    zIndex: 10,
                }}>
                    {imageSize.width} × {imageSize.height}
                </div>
            )}
        </div>
    );
};

const DialogPromptMatchWordPictureComponent: React.FC<DialogPromptMatchWordPictureComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [componentList, setComponentList] = React.useState<any[]>([]);
    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [chatBubbleCorrectDone, setChatBubbleCorrectDone] = React.useState(false);
    const [currentQuestionIndex, setCurrentQuestionIndex] = React.useState(0);
    const [shuffledQuestionIndexList, setShuffledQuestionIndexList] = React.useState<number[]>([]);
    const [currentVocabPrompt, setCurrentVocabPrompt] = React.useState('teacher_english_prompt');

    // preview dialog state (for image answer options)
    const [previewIndex, setPreviewIndex] = React.useState<number | null>(null);
    const shuffledImageWordListRef = React.useRef<IVocabularyWord[]>([]);
    const [shuffledImageWordListState, setShuffledImageWordListState] = React.useState<IVocabularyWord[]>([]);
    const previewVocabularyWord = previewIndex !== null ? shuffledImageWordListState[previewIndex] : null;

    // lightbox state (for question image)
    const [showLightbox, setShowLightbox] = React.useState(false);
    const [lightboxImageSrc, setLightboxImageSrc] = React.useState<string | undefined>(undefined);
    const [lightboxImageWord, setLightboxImageWord] = React.useState<string | undefined>(undefined);
    const [imageSize, setImageSize] = React.useState<{ width: number; height: number } | null>(null);

    const handlePrev = () => {
        setPreviewIndex(prev => {
            if (prev === null) return prev;
            return prev === 0 ? shuffledImageWordListState.length - 1 : prev - 1;
        });
    };

    const handleNext = () => {
        setPreviewIndex(prev => {
            if (prev === null) return prev;
            return prev === shuffledImageWordListState.length - 1 ? 0 : prev + 1;
        });
    };

    const touchStartX = React.useRef<number | null>(null);
    const touchEndX = React.useRef<number | null>(null);
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.targetTouches[0].clientX; };
    const onTouchMove = (e: React.TouchEvent) => { touchEndX.current = e.targetTouches[0].clientX; };
    const onTouchEnd = () => {
        if (touchStartX.current === null || touchEndX.current === null) return;
        const distance = touchStartX.current - touchEndX.current;
        if (distance > minSwipeDistance) handleNext();
        if (distance < -minSwipeDistance) handlePrev();
        touchStartX.current = null;
        touchEndX.current = null;
    };

    const studentAvatar = localStorage.getItem('studentAvatar');

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    // not sure how to handle this, because there is a list of dialogPrompt.vocabulary_word_list
    // const [studentResponseSentenceAudio, setStudentResponseSentenceAudio] = React.useState<Blob | null | undefined>(undefined);
    // const [teacherEnglishPromptAudio, setTeacherEnglishPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    // const [teacherChinesePromptAudio, setTeacherChinesePromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [yesResponseAudio, setYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [noResponseAudio, setNoResponseAudio] = React.useState<Blob | null | undefined>(undefined);

    // Ref so handleAnswerClick always sees the current answer list regardless of render closure
    const answerListRef = React.useRef<any[]>([]);

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
        // @TODO prompt audio is within each vocaublary word, not dialog prompt
        // getAudioByText(history, setErrorMessage, dialogPrompt.teacher_english_prompt, setTeacherEnglishPromptAudio)
        // getAudioByText(history, setErrorMessage, dialogPrompt.teacher_chinese_prompt, setTeacherChinesePromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.yes_response, setYesResponseAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.no_response, setNoResponseAudio)

        // for (const vocabularyWord of dialogPrompt.vocabulary_word_list) {
        //     const sentence = createStudentResponseSentence(vocabularyWord);
        //     setStudentResponseSentenceAudio(sentence)
        // }

        // randomize questions
        const n = dialogPrompt.vocabulary_word_list.length;
        const positiveNumbers = Array.from({ length: n }, (_, index) => index + 1);
        const negativeNumbers = Array.from({ length: n }, (_, index) => -index - 1);
        const combinedList = positiveNumbers.concat(negativeNumbers);
        const shuffledCombinedList = shuffle(combinedList);
        // if we have same word in a row, swap it with next.
        if (shuffledCombinedList.length > 2) {
            for (let i = 0; i < shuffledCombinedList.length - 1; i++) {
                if (Math.abs(shuffledCombinedList[i]) == Math.abs(shuffledCombinedList[i + 1])) {
                    if (i == 0) {
                        const swap = shuffledCombinedList[i + 1];
                        shuffledCombinedList[i + 1] = shuffledCombinedList[i + 2];
                        shuffledCombinedList[i + 2] = swap;
                    } else {
                        const swap = shuffledCombinedList[i];
                        shuffledCombinedList[i] = shuffledCombinedList[i - 1];
                        shuffledCombinedList[i - 1] = swap;
                    }
                }
            }

        }
        setShuffledQuestionIndexList(shuffledCombinedList); // Assuming you have a shuffle function (e.g., from Lodash)
    }, [clearFooterList, dialogPrompt.no_response, dialogPrompt.prompt, dialogPrompt.vocabulary_word_list.length, dialogPrompt.yes_response, history, setErrorMessage]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    }, [componentList, chatBubbleContainerRef])

    function createStudentResponseSentence(vocabularyWord: IVocabularyWord) {
        let sentence = vocabularyWord.word;
        if (vocabularyWord.student_response_prompt && vocabularyWord.student_response_prompt.length > 0) {
            sentence = vocabularyWord.student_response_prompt.replace(/<Answer\/>/g, vocabularyWord.word)
        }
        return sentence;
    }

    React.useEffect(() => {
        if (chatBubbleDone) {

            if (dialogPrompt.vocabulary_word_list.length == 0) {
                setChatBubbleDone(false);
                setCurrentVocabPrompt('done');
                setChatBubbleCorrectDone(true);
                return;
            }

            const currentQuestion = shuffledQuestionIndexList[currentQuestionIndex];
            const vocabularyWord = dialogPrompt.vocabulary_word_list[Math.abs(currentQuestion) - 1];
            const displayImageSelectWord = currentQuestion > 0;
            // console.log("currentQuestionIndex=" + currentQuestionIndex + " currentQuestion=" + currentQuestion + " vocabularyWord=" + vocabularyWord.word + " displayImageSelectWord=" + displayImageSelectWord + " currentVocabPrompt=" + currentVocabPrompt)
            if (currentVocabPrompt == 'teacher_english_prompt') {
                if (vocabularyWord.teacher_english_prompt) {
                    setChatBubbleDone(false);
                    const newChatBubble = (
                        <ChatBubble
                            isTeacher={true}
                            message={vocabularyWord.teacher_english_prompt}
                            messageAudio={null}
                            showAvatar={showAvatar}
                            chatBubbleContainerRef={chatBubbleContainerRef}
                            setChatBubbleDone={setChatBubbleDone}
                        />
                    );
                    setComponentList((prevComponentList) => [...prevComponentList, newChatBubble]);
                }
                setCurrentVocabPrompt('teacher_chinese_prompt')
            } else if (currentVocabPrompt == 'teacher_chinese_prompt') {
                if (vocabularyWord.teacher_chinese_prompt) {
                    setChatBubbleDone(false);
                    const newChatBubble = (
                        <ChatBubble
                            isTeacher={true}
                            message={vocabularyWord.teacher_chinese_prompt}
                            messageAudio={null}
                            showAvatar={showAvatar}
                            chatBubbleContainerRef={chatBubbleContainerRef}
                            setChatBubbleDone={setChatBubbleDone}
                            useChineseFont={true}
                        />
                    );
                    setComponentList((prevComponentList) => [...prevComponentList, newChatBubble]);
                }
                setCurrentVocabPrompt('student_answer')
            } else if (currentVocabPrompt == "student_answer") {
                setChatBubbleDone(false);
                if (displayImageSelectWord) {
                    // image-to-word mode: show image question, word buttons as answers
                    const imgSrc = vocabularyWord.image || vocabularyWord.image_url || undefined;
                    const promptImage = (
                        <div
                            key={vocabularyWord.id}
                            style={{ display: 'flex', justifyContent: 'center' }}
                        >
                            <QuestionImageDisplay
                                src={imgSrc}
                                alt={vocabularyWord.word}
                                onLightboxOpen={() => {
                                    setLightboxImageSrc(imgSrc);
                                    setLightboxImageWord(vocabularyWord.word);
                                    setImageSize(null);
                                    setShowLightbox(true);
                                }}
                            />
                        </div>
                    );
                    let newChatBubble = <></>
                    if (vocabularyWord.student_response_prompt && vocabularyWord.student_response_prompt.trim().toLowerCase() != "<answer/>") {
                        newChatBubble =
                            <ChatBubble
                                isTeacher={false}
                                message={vocabularyWord.student_response_prompt}
                                messageAudio={null} // no audio for student
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                useChineseFont={true}
                            />
                    };
                    setComponentList((prevComponentList) => [...prevComponentList, promptImage, newChatBubble]);
                    const wordAnswerList = dialogPrompt.vocabulary_word_list.map((vocabularyWordOption) => (
                        <Button
                            variant="contained"
                            key={vocabularyWordOption.id}
                            sx={{ m: 1 }}
                            onClick={() => handleAnswerClick(vocabularyWordOption.id, vocabularyWordOption.id === vocabularyWord.id)}
                        >
                            <Typography style={{ fontFamily: 'KaiTi', fontSize: '24px' }}>
                                {vocabularyWordOption.word}
                            </Typography>
                        </Button>
                    ));
                    answerListRef.current = shuffle(wordAnswerList.filter(Boolean));
                    setActionList(answerListRef.current)
                } else {
                    // word-to-image mode: show word question, image options as answers
                    const studentResponseSentence = createStudentResponseSentence(vocabularyWord)
                    const newChatBubble = (
                        <ChatBubble
                            isTeacher={true}
                            message={studentResponseSentence}
                            messageAudio={null} // TODO how to do this? array of vocaublary_list?
                            showAvatar={showAvatar}
                            chatBubbleContainerRef={chatBubbleContainerRef}
                            useChineseFont={true}
                        />
                    );
                    setComponentList((prevComponentList) => [...prevComponentList, newChatBubble]);
                    const shuffledVocabList = shuffle([...dialogPrompt.vocabulary_word_list]);
                    shuffledImageWordListRef.current = shuffledVocabList;
                    setShuffledImageWordListState(shuffledVocabList);
                    const imageList = shuffledVocabList.map((vocabularyWordOption, index) => (
                        <Box
                            key={vocabularyWordOption.id}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                m: 1,
                                gap: 1,
                                position: 'relative',
                                zIndex: 1,
                                transition: 'all 0.2s ease',
                                '&:hover': { transform: 'translateY(-2px)', zIndex: 10 },
                            }}
                        >
                            {(vocabularyWordOption.image || vocabularyWordOption.image_url) ? (
                                <img
                                    src={vocabularyWordOption.image || vocabularyWordOption.image_url || undefined}
                                    alt={vocabularyWordOption.word}
                                    onClick={() => setPreviewIndex(index)}
                                    style={{
                                        maxHeight: 125,
                                        maxWidth: 125,
                                        height: 'auto',
                                        width: 'auto',
                                        objectFit: 'contain',
                                        cursor: 'pointer',
                                        borderRadius: '12px',
                                        transition: 'transform 0.2s ease, filter 0.2s ease',
                                        filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))',
                                    }}
                                    onMouseOver={(e) => {
                                        (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.08)';
                                        (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 4px 16px rgba(0,0,0,0.12))';
                                    }}
                                    onMouseOut={(e) => {
                                        (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)';
                                        (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))';
                                    }}
                                />
                            ) : null}
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleAnswerClick(vocabularyWordOption.id, vocabularyWordOption.id === vocabularyWord.id)}
                                sx={{
                                    minWidth: 80,
                                    fontSize: '0.8rem',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    padding: '6px 16px',
                                    lineHeight: 1.2,
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: '#0062FF',
                                        color: '#fff',
                                        borderColor: '#0062FF',
                                    }
                                }}
                            >
                                Select
                            </Button>
                        </Box>
                    ));
                    answerListRef.current = imageList.filter(Boolean);
                    setActionList(answerListRef.current)
                }
                setCurrentVocabPrompt('done')
            }
        }
    // The legacy prompt advances through a state machine; expanding these deps causes duplicate bubbles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatBubbleDone, currentVocabPrompt, currentQuestionIndex])

    const handleAnswerClick = (vocabularyWordId: number, correct: boolean) => {
        const currentQuestion = shuffledQuestionIndexList[currentQuestionIndex];
        const displayImageSelectWord = currentQuestion > 0;

        let foundAnswerCopy;
        if (displayImageSelectWord) {
            const foundAnswer = answerListRef.current.find((answer) => answer.key == vocabularyWordId);
            foundAnswerCopy = <ChatBubble
                isTeacher={false}
                message={foundAnswer.props.children.props.children}
                messageAudio={null} // no audio for student answers
                showAvatar={false} // avatar manually added below
                chatBubbleContainerRef={chatBubbleContainerRef}
                useChineseFont={true}
            />
        } else {
            const foundVocabWord = dialogPrompt.vocabulary_word_list.find(w => w.id === vocabularyWordId);
            foundAnswerCopy = (
                <img
                    src={foundVocabWord?.image || foundVocabWord?.image_url || undefined}
                    alt={foundVocabWord?.word}
                    style={{ maxHeight: '200px', maxWidth: '200px', height: 'auto', width: 'auto' }}
                />
            );
        }
        const stackFoundAnswerCopy =
            <Stack direction="row" alignItems="center" justifyContent="right" spacing={2}>
                {foundAnswerCopy}
                <img
                    src={studentAvatar || ''}
                    style={{ maxHeight: '4em', maxWidth: '4em' }}
                />
            </Stack>
        if (correct) {
            if (currentQuestionIndex + 1 >= shuffledQuestionIndexList.length) {
                setChatBubbleCorrectDone(true);
                clearFooterList()
            } else {
                setCurrentVocabPrompt('teacher_english_prompt')
                setCurrentQuestionIndex((prevIndex) => prevIndex + 1)
            }

            const newChatBubble = (
                <ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.yes_response}
                    messageAudio={yesResponseAudio ? yesResponseAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setChatBubbleDone}
                />
            );
            setComponentList((prevComponentList) => [...prevComponentList, stackFoundAnswerCopy, newChatBubble]);
            UserDialogPromptApi.addCorrectAnswer(dialogPrompt.id)
                .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
        } else {
            if (!displayImageSelectWord) {
                const reshuffled = shuffle([...shuffledImageWordListRef.current]);
                shuffledImageWordListRef.current = reshuffled;
                setShuffledImageWordListState(reshuffled);
                const currentVocabWord = dialogPrompt.vocabulary_word_list[Math.abs(currentQuestion) - 1];
                const newImageList = reshuffled.map((vocabularyWordOption, index) => (
                    <Box key={vocabularyWordOption.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', m: 1, gap: 1, position: 'relative', zIndex: 1, transition: 'all 0.2s ease', '&:hover': { transform: 'translateY(-2px)', zIndex: 10 } }}>
                        {(vocabularyWordOption.image || vocabularyWordOption.image_url) ? (
                            <img
                                src={vocabularyWordOption.image || vocabularyWordOption.image_url || undefined}
                                alt={vocabularyWordOption.word}
                                onClick={() => setPreviewIndex(index)}
                                style={{ maxHeight: 125, maxWidth: 125, height: 'auto', width: 'auto', objectFit: 'contain', cursor: 'pointer', borderRadius: '12px', transition: 'transform 0.2s ease, filter 0.2s ease', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))' }}
                                onMouseOver={(e) => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.08)'; (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 4px 16px rgba(0,0,0,0.12))'; }}
                                onMouseOut={(e) => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)'; (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))'; }}
                            />
                        ) : null}
                        <Button variant="outlined" size="small" onClick={() => handleAnswerClick(vocabularyWordOption.id, vocabularyWordOption.id === currentVocabWord.id)} sx={{ minWidth: 80, fontSize: '0.8rem', textTransform: 'none', borderRadius: '8px', padding: '6px 16px', lineHeight: 1.2, fontWeight: 500, transition: 'all 0.2s ease', '&:hover': { backgroundColor: '#0062FF', color: '#fff', borderColor: '#0062FF' } }}>
                            Select
                        </Button>
                    </Box>
                ));
                answerListRef.current = newImageList.filter(Boolean);
                setActionList(answerListRef.current);
            } else {
                answerListRef.current = shuffle(answerListRef.current.filter(Boolean));
                setActionList(answerListRef.current);
            }
            setCurrentVocabPrompt('student_match')

            const newChatBubble = (
                <ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.no_response}
                    messageAudio={noResponseAudio ? noResponseAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setChatBubbleDone}
                />
            );
            setComponentList((prevComponentList) => [...prevComponentList, stackFoundAnswerCopy, newChatBubble]);
            UserDialogPromptApi.addIncorrectAnswer(dialogPrompt.id)
                .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })

        }
    }

    React.useEffect(() => {
        if (chatBubbleCorrectDone) {
            setDialogPromptComplete(dialogPrompt.id)
        }
    }, [chatBubbleCorrectDone, dialogPrompt.id, setDialogPromptComplete])

    return (
        <Stack spacing={2}>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
            />
            {componentList.map((component, index) => (
                <Box key={index} component="div">
                    {component}
                </Box>
            ))}

            {/* Lightbox for question image (image-to-word mode) */}
            {showLightbox && (
                <div
                    onClick={() => setShowLightbox(false)}
                    style={{
                        position: 'fixed',
                        top: 0, left: 0,
                        width: '100vw', height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 9999,
                        margin: 0, padding: 0,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', cursor: 'default' }}
                    >
                        <img
                            src={lightboxImageSrc}
                            alt={lightboxImageWord}
                            onLoad={(e) => {
                                const img = e.currentTarget;
                                setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
                            }}
                            style={{
                                maxWidth: '90vw', maxHeight: '90vh',
                                objectFit: 'contain',
                                borderRadius: '10px',
                                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.5)',
                            }}
                        />
                        {imageSize && (
                            <div style={{ color: '#ccc', fontSize: '12px', paddingRight: '4px' }}>
                                {imageSize.width} × {imageSize.height}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Preview dialog for image answer options (word-to-image mode) */}
            <Dialog
                open={previewIndex !== null}
                onClose={() => setPreviewIndex(null)}
                fullScreen
                PaperProps={{
                    sx: { backgroundColor: 'rgba(0, 0, 0, 0.96)', color: '#fff' }
                }}
            >
                <DialogContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        height: '100vh',
                        overflow: 'hidden',
                        p: 0,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                        }}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <IconButton
                            aria-label="Previous image"
                            onClick={handlePrev}
                            sx={{
                                position: 'absolute', left: 16,
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.35)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.55)' },
                                width: 48, height: 48,
                            }}
                        >
                            ‹
                        </IconButton>

                        {previewVocabularyWord && (
                            <img
                                src={previewVocabularyWord.image_url || previewVocabularyWord.image || "broken"}
                                alt={previewVocabularyWord.word}
                                style={{
                                    maxWidth: '95vw', maxHeight: '85vh',
                                    width: 'auto', height: 'auto',
                                    objectFit: 'contain',
                                    borderRadius: '14px',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
                                    display: 'block',
                                }}
                            />
                        )}

                        <IconButton
                            aria-label="Next image"
                            onClick={handleNext}
                            sx={{
                                position: 'absolute', right: 16,
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.35)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.55)' },
                                width: 48, height: 48,
                            }}
                        >
                            ›
                        </IconButton>

                        {previewIndex !== null && (
                            <Box
                                sx={{
                                    position: 'absolute', bottom: 16,
                                    left: '50%', transform: 'translateX(-50%)',
                                    px: 2, py: 0.5,
                                    borderRadius: '999px',
                                    backgroundColor: 'rgba(0,0,0,0.45)',
                                    color: '#ddd', fontSize: '13px',
                                    backdropFilter: 'blur(8px)',
                                }}
                            >
                                {previewIndex + 1} / {shuffledImageWordListState.length}
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setPreviewIndex(null)} sx={{ color: '#ccc' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (previewVocabularyWord) {
                                const currentQuestion = shuffledQuestionIndexList[currentQuestionIndex];
                                const currentVocabWord = dialogPrompt.vocabulary_word_list[Math.abs(currentQuestion) - 1];
                                handleAnswerClick(previewVocabularyWord.id, previewVocabularyWord.id === currentVocabWord?.id);
                                setPreviewIndex(null);
                            }
                        }}
                        className="secondaryButton"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default DialogPromptMatchWordPictureComponent;
